import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import {
  upsertMediaManagerTemplateIntoCache,
  useCopyMediaManagerTemplateMutation,
} from 'modules/manager/store/media-manager-api';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';

export interface DuplicateTemplateOptions {
  /** Name for the copy. Blank/omitted falls back to `<source> (копия)`. */
  name?: string;
}

export interface UseDuplicateTemplateResult {
  /** True when there is an active tournament to copy into. */
  canDuplicate: boolean;
  /** True while a copy is being created — callers must block their duplicate control. */
  isDuplicating: boolean;
  /** Message for the last failed attempt (e.g. the name is taken), else null. */
  error: string | null;
  /** Clone a template (preset) into the active tournament as a private copy. */
  duplicate: (templateId: number, options?: DuplicateTemplateOptions) => Promise<MediaManagerTemplate | null>;
  /** Clear `error` — call when the form is reopened. */
  resetError: () => void;
}

/**
 * Copies a template (typically a global preset) into the current tournament so
 * the user can edit their own copy. Requires an active tournament.
 *
 * The copy goes through RTK Query, not the repository: the templates list is
 * served from the RTK Query cache, and a repository call left that cache stale —
 * the new template only appeared after a page reload. The returned row is pushed
 * into the cache directly, so nothing is refetched on a successful copy.
 */
export function useDuplicateTemplate(): UseDuplicateTemplateResult {
  const tournamentId = useSelector(authStoreUserTournamentId);
  const dispatch = useAppDispatch();
  const [copyTemplate] = useCopyMediaManagerTemplateMutation();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A ref rather than the state flag: two clicks in the same tick both read the
  // stale `false` from state before React re-renders the disabled control, so
  // only a synchronous guard actually prevents a second copy.
  const inFlight = useRef(false);

  const duplicate = useCallback(
    async (templateId: number, options?: DuplicateTemplateOptions): Promise<MediaManagerTemplate | null> => {
      if (!tournamentId) {
        console.error('Cannot duplicate a template without an active tournament');
        return null;
      }
      if (inFlight.current) return null;

      inFlight.current = true;
      setIsDuplicating(true);
      setError(null);
      try {
        const name = options?.name?.trim();
        const copy = await copyTemplate({ templateId, tournamentId, name: name || undefined }).unwrap();
        dispatch(upsertMediaManagerTemplateIntoCache(copy));
        return copy;
      } catch (err) {
        console.error('Failed to duplicate media manager template:', err);
        // The DB owns name uniqueness, so a taken name arrives here rather than
        // from a client-side check — it must read as a form error, not a dead button.
        setError(isTemplateNameConflict(err) ? TEMPLATE_NAME_CONFLICT_MESSAGE : 'Не удалось создать копию, попробуйте ещё раз');
        return null;
      } finally {
        inFlight.current = false;
        setIsDuplicating(false);
      }
    },
    [tournamentId, copyTemplate, dispatch],
  );

  const resetError = useCallback(() => setError(null), []);

  return { canDuplicate: !!tournamentId, isDuplicating, error, duplicate, resetError };
}
